import Icon, { IconsVariant } from "@/Components/Icon/Icon";
import { cn } from "@/shared/utils/cn";

interface TagChipProps {
  name: string;
  active?: boolean;
  icon?: IconsVariant;
  count?: number;
  onClick?: () => void;
  /** Renders a remove button. Only unlinks the tag, never deletes it. */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}

const TagChip = ({
  name,
  active = false,
  icon,
  count,
  onClick,
  onRemove,
  removeLabel,
  className,
}: TagChipProps) => {
  const chipClassName = cn(
    "inline-flex max-w-full items-center gap-2 rounded-full border px-4 py-1 text-[1.25rem] font-medium leading-[1.8rem] whitespace-nowrap transition-colors",
    active
      ? "border-primary bg-primary text-white"
      : "border-border bg-light text-text",
    onClick &&
      !active &&
      "cursor-pointer hover:border-primary hover:text-primary focus-visible:border-primary",
    onClick && active && "cursor-pointer hover:opacity-90",
    "focus-visible:outline-none focus-visible:shadow-[0_0_0_0.2rem_var(--primary)]",
    className
  );

  const content = (
    <>
      {icon && <Icon className="size-4 shrink-0 fill-current" variants={icon} />}
      <span className="min-w-0 truncate">{name}</span>
      {count !== undefined && (
        <span className={cn("text-[1.1rem]", active ? "opacity-80" : "text-text-secondary")}>
          {count}
        </span>
      )}
    </>
  );

  if (onRemove) {
    return (
      <span className={cn(chipClassName, "pr-2")}>
        {content}
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          title={removeLabel}
          className="flex h-[1.8rem] w-[1.8rem] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 hover:bg-[rgba(0,0,0,0.08)] focus-visible:outline-none focus-visible:shadow-[0_0_0_0.2rem_var(--primary)]"
        >
          <Icon
            className={cn("h-[0.9rem] w-[0.9rem]", active ? "fill-white" : "fill-text")}
            variants="times"
          />
        </button>
      </span>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-pressed={active} className={chipClassName}>
        {content}
      </button>
    );
  }

  return <span className={chipClassName}>{content}</span>;
};

export default TagChip;
